import { all, call, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { isApiError, requestPuppy, requestNapi, XhrResponse } from '@wix/da-http-client';
import {
  groupJoinFail,
  groupJoinPending,
  groupJoinSuccess,
  groupLeaveFail,
  groupLeaveSuccess,
  requestJoinGroup,
  requestLeaveGroup,
  messageAdmins,
  sendAdminNote,
} from './actions';
import {
  PapiDaSharedGroupJoin,
  PapiDaSharedGroupLeave,
  PapiRequestDaSharedGroupJoin,
  PapiRequestDaSharedGroupLeave,
  PapiDaSharedGroupNoteSend,
  PapiUser,
} from '@wix/da-papi-types';
import history from '../../redux/routing/history';
import { Url, URLS, USER_PROFILE_BACKROOM_SECTIONS } from '@wix/da-url';
import { LeaveGroup as LeaveGroupIcon } from '@wix/da-ds/pkg/Icons/Confirmations';
import { Success as SuccessIcon } from '@wix/da-ds/pkg/Icons/Confirmations/Success';
import { Warning as WarningIcon } from '@wix/da-ds/pkg/Icons/Confirmations/Warning';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications/redux/types';
import {
  confirmNo,
  confirmYes,
  pushGlobalConfirmationModal,
  popModal,
  pushModal,
} from '../../Modals/redux/actionCreators';
import { getUserWatchState } from '../../WatchButton/redux/selectors';
import i18next from 'i18next';
import { userSchema } from '../normalizr/schemas/user';
import { normalize } from '../normalizr';
import { putEntities } from '../entities/actions';
import { ModalType } from '../../Modals/redux/types';

function* handleClosedGroupJoinRequest(group: PapiUser) {
  const groupWatchState: ReturnType<typeof getUserWatchState> = yield select(
    getUserWatchState,
    group
  );

  if (groupWatchState.isWatching) {
    yield put(
      addNotification(NotificationType.Generic, {
        message: i18next.t('group.join.errors.closed', {
          groupname: group.username,
        }),
        isError: true,
        cardProps: {
          multiLine: true,
          duration: 5000,
        },
      })
    );

    return;
  }

  yield put(
    pushModal(ModalType.GROUP_CLOSED_WATCH, {
      params: { group },
      wrapInModal: false,
    })
  );
}

function* handleGroupJoinRequest({ payload: { group } }: ReturnType<typeof requestJoinGroup>) {
  if (!group.extendedGroup?.apps?.join?.canJoin) {
    yield call(handleClosedGroupJoinRequest, group);
    return;
  }

  const roleId = group.extendedGroup?.apps?.join.intoRoleid;
  if (!roleId) {
    return;
  }
  const data: PapiRequestDaSharedGroupJoin = {
    groupid: group.userId,
    roleid: roleId,
  };
  const result: PapiDaSharedGroupJoin | false = yield call(requestPuppy, {
    method: 'post',
    url: 'group/join',
    data,
  });

  if (!result || !result.success || !result.thisGroup) {
    yield all([
      put(
        addNotification(NotificationType.Generic, {
          message: result && result.errorDescription,
          messageLocaleKey: result
            ? `group.join.errors.${result.errorDescription}`
            : 'group.join.errors.generic',
          isError: true,
          cardProps: {
            multiLine: true,
            duration: 5000,
          },
        })
      ),
      put(
        groupJoinFail(
          group,
          (result && result.errorDescription) || i18next.t('group.join.errors.generic')
        )
      ),
    ]);
    return;
  }

  const { entities } = normalize(result.thisGroup, userSchema);
  yield put(putEntities({ entities }));

  if (result.thisGroup.extendedGroup?.isIngroup) {
    yield put(groupJoinSuccess(result.thisGroup));
    return;
  }

  yield all([
    put(groupJoinPending(result.thisGroup)),
    put(
      pushModal(ModalType.GROUP_JOIN_PENDING, {
        params: { group },
        wrapInModal: false,
      })
    ),
  ]);
}

function* handleGroupLeaveRequest({ payload: { group } }: ReturnType<typeof requestLeaveGroup>) {
  const extendedGroup = group?.extendedGroup;
  const canLeave = extendedGroup?.apps?.join?.canLeave;
  const inGroup = extendedGroup?.isIngroup;

  if (inGroup && !canLeave) {
    const profileSection = history.location.pathname.split('/')[2];
    const inBackroom = profileSection === 'backroom';
    if (!inBackroom) {
      yield put(
        pushGlobalConfirmationModal({
          titleTransKey: 'group.leave.founder.error.heading',
          textTransKey: 'group.leave.founder.error.body',
          confirmBtnLabelTransKey: 'group.leave.founder.button.accept',
          confirmBtnType: 'danger',
          translationOptions: { groupname: group.username },
        })
      );
      const { leave } = yield race({
        leave: take(confirmYes),
        cancel: take(confirmNo),
      });
      if (!leave) {
        return;
      }

      // redirect to group backroom
      window.location.href = Url.groupBackroomLink(
        group.username,
        USER_PROFILE_BACKROOM_SECTIONS.ROOT,
        '',
        {
          flow: 'founder-leave',
        }
      );
      return;
    }
  }

  // Confirm Leaving
  yield put(
    pushGlobalConfirmationModal({
      cancelBtnLabelTransKey: 'common.cancel',
      confirmBtnLabelTransKey: 'group.leave.confirm.button.leave',
      textTransKey: 'group.leave.confirm.body',
      titleTransKey: 'group.leave.confirm.heading',
      confirmBtnType: 'default',
      translationOptions: { groupname: group.username },
      icon: <LeaveGroupIcon />,
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    return;
  }

  const data: PapiRequestDaSharedGroupLeave = {
    groupid: group.userId,
  };
  const result: PapiDaSharedGroupLeave | false = yield call(requestPuppy, {
    method: 'post',
    url: 'group/leave',
    data,
  });

  if (!result || !result.success || !result.thisGroup) {
    yield all([
      put(
        addNotification(NotificationType.Generic, {
          message: result && result.errorDescription,
          messageLocaleKey: result
            ? `group.leave.errors.${result.errorDescription}`
            : 'group.leave.errors.generic',
          isError: true,
          cardProps: {
            multiLine: true,
            duration: 5000,
          },
        })
      ),
      put(
        groupLeaveFail(
          group,
          (result && result.errorDescription) || i18next.t('group.leave.errors.generic')
        )
      ),
    ]);
    return;
  }

  const { entities } = normalize(result.thisGroup, userSchema);
  yield all([put(putEntities({ entities })), put(groupLeaveSuccess(result.thisGroup))]);

  // Redirect to rootpage
  window.location.assign(URLS.ROOT);
}

function* handleMessageAdmins({ payload: { group } }: ReturnType<typeof messageAdmins>) {
  yield put(
    pushModal(ModalType.GROUP_MESSAGE_ADMINS, {
      params: { recipientId: group.userId },
      wrapInModal: false,
    })
  );
  return;
}

function* handleSendAdminNote({
  payload: { recipientId, subject, editorRaw },
}: ReturnType<typeof sendAdminNote>) {
  const data = {
    recipientid: recipientId,
    subject,
    editorRaw: JSON.stringify(editorRaw),
  };

  const response: XhrResponse<PapiDaSharedGroupNoteSend> = yield call(requestNapi, {
    method: 'post',
    url: '/group/note/send',
    data,
  });

  if (!response || isApiError(response) || (response as any).errorCode === 0) {
    // Message Error!
    yield put(
      pushGlobalConfirmationModal({
        cancelBtnLabelTransKey: 'common.cancel',
        confirmBtnLabelTransKey: 'common.ok',
        textTransKey: 'popup.messageAdmins.confirm.error.text',
        titleTransKey: 'popup.messageAdmins.confirm.error.title',
        confirmBtnType: 'default',
        variant: 'vertical',
        icon: <WarningIcon />,
      })
    );
    return;
  }

  yield put(popModal());

  // Message Sent!
  yield put(
    pushGlobalConfirmationModal({
      cancelBtnLabelTransKey: 'common.cancel',
      confirmBtnLabelTransKey: 'common.close',
      textTransKey: 'popup.messageAdmins.confirm.sent.text',
      titleTransKey: 'popup.messageAdmins.confirm.sent.title',
      confirmBtnType: 'default',
      variant: 'vertical',
      icon: <SuccessIcon />,
    })
  );
}

export default function* groupsSagaRoot() {
  yield all([
    takeEvery(requestJoinGroup, handleGroupJoinRequest),
    takeEvery(requestLeaveGroup, handleGroupLeaveRequest),
    takeEvery(messageAdmins, handleMessageAdmins),
    takeEvery(sendAdminNote, handleSendAdminNote),
  ]);
}
